/**
 * Parses the backend's `aligned_allegations_json` — a list of JSON-encoded
 * AlignedAllegation strings published at the alignment node boundary — into a
 * light shape the UI can use for case-specific "thinking" and the Analysis tab
 * before the full findings (with determinations + demand) arrive.
 */
export interface ParsedAllegation {
  id: string
  title: string
  provisions: string[]
  /**
   * Best-effort amount in dispute, in rupees. AlignedAllegation carries NO
   * amount field (the SCN allegation's `amount_involved_total` is not copied
   * across at alignment), so this is extracted from currency figures stated in
   * the one-line SCN description — null when the prose states none. Display
   * code must degrade gracefully on null rather than invent a figure.
   */
  amount: number | null
}

const CURRENCY_RE = /(?:₹|\bRs\.?|\bINR\b)\s*([0-9][0-9,]*(?:\.[0-9]+)?)\s*(lakhs?|lacs?|crores?|cr\.?)?/gi

/**
 * Extract the amount in dispute from an allegation's one-line SCN description.
 * Conservative: only currency-marked figures (₹ / Rs. / INR) count, optional
 * lakh/crore multipliers are honoured, and with several figures the largest is
 * taken (a total exceeds its components). Returns null when nothing is stated.
 */
export function extractAmountInDispute(text: string | null | undefined): number | null {
  if (!text) return null
  let best: number | null = null
  for (const match of text.matchAll(CURRENCY_RE)) {
    const raw = Number(match[1].replace(/,/g, ""))
    if (!Number.isFinite(raw) || raw <= 0) continue
    const unit = (match[2] ?? "").toLowerCase()
    const multiplier = unit.startsWith("cr") ? 1e7 : unit.startsWith("la") ? 1e5 : 1
    const value = raw * multiplier
    if (best === null || value > best) best = value
  }
  return best
}

export function parseAlignedAllegations(json: string[] | null | undefined): ParsedAllegation[] {
  if (!Array.isArray(json)) return []
  const out: ParsedAllegation[] = []
  for (const item of json) {
    try {
      const a = typeof item === "string" ? JSON.parse(item) : item
      if (!a || typeof a !== "object") continue
      const id = String(a.allegation_id ?? "").trim()
      const title = String(a.scn_description ?? "").trim()
      if (!id && !title) continue
      out.push({
        id: id || `A-${out.length + 1}`,
        title: title || id,
        provisions: Array.isArray(a.scn_legal_provisions) ? a.scn_legal_provisions : [],
        amount: extractAmountInDispute(title),
      })
    } catch {
      /* skip malformed entries (partial stream chunks etc.) */
    }
  }
  return out
}
